import React, { useState } from 'react';
import { toast } from 'sonner';
import { Eye, CheckCircle2, XCircle, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { CollectorResolvePanel } from './CollectorResolvePanel';
import { CollectorCorrectionModal } from './CollectorCorrectionModal';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { approveCorrectionRequest, rejectCorrectionRequest, markDuplicateCorrectionRequest } from '../../utils/taxCollectorsApi';

// Same labels/semantics as pages/CollectorCorrectionRequestsPage.jsx (kept for rollback).
export const ISSUE_TYPE_LABELS = {
  missing_collector: 'Missing collector', incorrect_name: 'Incorrect name', incorrect_address: 'Incorrect address',
  incorrect_phone: 'Incorrect phone', wrong_collector_entirely: 'Wrong collector entirely', other: 'Other',
};
export const PRIORITY_META = {
  high: { dot: 'bg-red-500', label: 'High' }, medium: { dot: 'bg-amber-400', label: 'Medium' }, low: { dot: 'bg-emerald-500', label: 'Low' },
};
export const PriorityPill = ({ bucket }) => {
  const m = PRIORITY_META[bucket] || PRIORITY_META.low;
  return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700" data-testid={`priority-${bucket || 'low'}-pill`}><span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.label}</span>;
};


/** View / Approve / Reject / Mark duplicate for ONE collector-correction row. Approve marks the request approved and
 *  opens the Tax Collectors editor in a new tab for the manual fix — no collector data is changed automatically. */
export const CorrectionReviewActions = ({ row, isReviewer, onChanged, formatDate }) => {
  const [view, setView] = useState(false);
  const [resolvedPending, setResolvedPending] = useState(false);  // P2: refresh the list only when the drawer closes so the confirmation stays visible
  const [busy, setBusy] = useState(false);
  const [reject, setReject] = useState(null);
  const [dup, setDup] = useState(null);
  const pending = row.status === 'pending';
  const done = async (p) => { await p; onChanged?.(); };

  const approve = async () => {
    setBusy(true);
    try {
      await approveCorrectionRequest(row.id, { resolution_action: 'Marked approved from queue. Edit collector / assignment manually as needed.' });
      toast.success('Request approved. Update the collector or assignment record next.');
      window.open('/admin/jurisdictions/tax-collectors', '_blank', 'noopener');
      await done(Promise.resolve()); setView(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to approve'); } finally { setBusy(false); }
  };
  const doReject = async () => {
    if ((reject || '').trim().length < 5) return toast.error('Rejection notes must be at least 5 characters.');
    setBusy(true);
    try { await done(rejectCorrectionRequest(row.id, reject.trim())); toast.success('Request rejected'); setReject(null); setView(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to reject'); } finally { setBusy(false); }
  };
  const doDup = async () => {
    if (!dup?.target?.trim()) return toast.error('Enter the canonical request id.');
    setBusy(true);
    try { await done(markDuplicateCorrectionRequest(row.id, { duplicate_of_id: dup.target.trim(), review_notes: dup.notes?.trim() || null })); toast.success('Request marked as duplicate'); setDup(null); setView(false); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to mark duplicate'); } finally { setBusy(false); }
  };
  const stop = (e) => e.stopPropagation();
  const isAutoAssign = row.source === 'pa_auto_assign';  // PA-COLLECTOR-AUTOMATION-001 P2 — resolved via CollectorResolvePanel only

  return (
    <div className="inline-flex items-center justify-end gap-1" onClick={stop}>
      <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs rounded-lg" onClick={() => setView(true)} title="View details" data-testid={`ccr-view-${row.id}`}><Eye className="h-3.5 w-3.5" /><span className="hidden min-[1700px]:inline ml-1">View</span></Button>
      {isReviewer && pending && isAutoAssign && (
        <Button size="sm" className="h-8 px-2.5 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setView(true)} title="Resolve collector" data-testid={`ccr-resolve-${row.id}`}><CheckCircle2 className="h-3.5 w-3.5" /><span className="ml-1">Resolve</span></Button>
      )}
      {isReviewer && pending && !isAutoAssign && (<>
        <Button size="sm" className="h-8 px-2.5 text-xs rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy} onClick={approve} title="Approve" data-testid={`ccr-approve-${row.id}`}><CheckCircle2 className="h-3.5 w-3.5" /><span className="hidden min-[1700px]:inline ml-1">Approve</span></Button>
        <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50" disabled={busy} onClick={() => setReject('')} title="Reject" data-testid={`ccr-reject-${row.id}`}><XCircle className="h-3.5 w-3.5" /><span className="hidden min-[1700px]:inline ml-1">Reject</span></Button>
        <Button size="sm" variant="ghost" className="h-8 px-2 rounded-lg text-slate-500" disabled={busy} onClick={() => setDup({ target: '', notes: '' })} title="Mark duplicate" data-testid={`ccr-duplicate-${row.id}`}><Copy className="h-3.5 w-3.5" /></Button>
      </>)}

      {/* COLLECTOR-CORRECTION-PREMIUM-UI-001 — centered premium modal replaces the right-side drawer (handlers/testids unchanged) */}
      <CollectorCorrectionModal
        open={view}
        onOpenChange={(o) => { setView(o); if (!o && resolvedPending) { setResolvedPending(false); done(Promise.resolve()); } }}
        row={row}
        testId="ccr-drawer"
        zIndexClass="z-[121]"
        footer={(
          <div className="ccr-footer justify-between">
            <a href="/admin/jurisdictions/tax-collectors" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:underline" data-testid="ccr-open-editor"><ExternalLink className="h-3.5 w-3.5" />Tax Collectors editor</a>
            {isReviewer && pending && !isAutoAssign && <div className="flex gap-2"><Button size="sm" variant="outline" className="rounded-xl text-rose-600 border-rose-200" disabled={busy} onClick={() => setReject('')}>Reject</Button><Button size="sm" className="ccr-cta !h-10" disabled={busy} onClick={approve}>Approve</Button></div>}
          </div>
        )}
      >
        {isAutoAssign && isReviewer && pending && <CollectorResolvePanel row={row} onResolved={() => setResolvedPending(true)} />}
      </CollectorCorrectionModal>

      <Dialog open={reject !== null} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="z-[131]" data-testid="ccr-reject-dialog">
          <DialogHeader><DialogTitle>Reject collector correction</DialogTitle></DialogHeader>
          <Textarea value={reject || ''} onChange={(e) => setReject(e.target.value)} placeholder="Reason (min 5 characters)" rows={4} data-testid="ccr-reject-notes" />
          <DialogFooter><Button variant="outline" onClick={() => setReject(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy} onClick={doReject} data-testid="ccr-reject-confirm">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dup !== null} onOpenChange={(o) => !o && setDup(null)}>
        <DialogContent className="z-[131]" data-testid="ccr-duplicate-dialog">
          <DialogHeader><DialogTitle>Mark as duplicate</DialogTitle></DialogHeader>
          <Input value={dup?.target || ''} onChange={(e) => setDup({ ...dup, target: e.target.value })} placeholder="Canonical request id" data-testid="ccr-duplicate-target" />
          <Textarea value={dup?.notes || ''} onChange={(e) => setDup({ ...dup, notes: e.target.value })} placeholder="Notes (optional)" rows={3} data-testid="ccr-duplicate-notes" />
          <DialogFooter><Button variant="outline" onClick={() => setDup(null)}>Cancel</Button><Button disabled={busy} onClick={doDup} data-testid="ccr-duplicate-confirm">Mark duplicate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
